import { supabase } from './supabase';
import { resolveContaSicoob, type ContaSicoob } from './sicoobAccount';
import { classificarRespostaCancelamento } from './cancelamentoSicoobResposta';

/**
 * Cancelamento de boleto no Sicoob — fonte única.
 *
 * O bloco vivia duplicado literalmente em CriarParcelamentoModal e
 * QuitarFaturasModal, e os dois tinham o mesmo defeito: qualquer resposta que não
 * fosse 2xx virava "falha" e sumia num toast. Duas consequências reais:
 *
 * 1. `Boleto já baixado/liquidado` era contado como falha e alarmava o operador —
 *    mas é justamente o estado que a gente quer. Cancelar de novo um boleto já
 *    baixado é no-op, não erro.
 * 2. Quando a falha era de verdade, a fatura virava Parcelado/Quitado assim mesmo
 *    e o boleto continuava vivo no banco. Passado o toast, ninguém mais sabia —
 *    até entrar dinheiro sem fatura correspondente.
 *
 * Aqui a falha é classificada e **gravada na própria fatura**
 * (`cancelamento_sicoob_erro`), virando fila de conferência em vez de aviso
 * efêmero. Ver migration 20260811150000.
 */

export interface FaturaCancelavel {
    id: number | string;
    uc_codigo?: string | null;
    mes_referencia?: string | null;
    num_cobranca_boleto?: string | null;
    conta_sicoob?: string | null;
}

export interface ResultadoCancelamento {
    fatura_id: number | string;
    mes_referencia: string;
    nosso_numero: string | null;
    conta_sicoob: ContaSicoob | null;
    ok: boolean;
    /** true quando o Sicoob respondeu que o boleto já estava baixado — sucesso, não erro. */
    jaEstavaBaixado: boolean;
    detalhe: string;
}

export interface ResumoCancelamento {
    resultados: ResultadoCancelamento[];
    falhas: ResultadoCancelamento[];
    /** Rótulos das faturas que falharam, prontos para o toast. */
    rotulosFalha: string[];
}

// A regra que decide se um erro do Sicoob é, na verdade, sucesso, mora em
// cancelamentoSicoobResposta.ts — módulo sem I/O, coberto por teste.

/**
 * Cancela os boletos das faturas informadas e registra o resultado em cada uma.
 *
 * Não lança: uma falha de rede numa fatura não pode abortar o parcelamento
 * inteiro. O chamador decide o que fazer com `falhas`.
 */
export async function cancelarBoletosSicoob(
    faturas: FaturaCancelavel[],
    motivoCancelamento: string
): Promise<ResumoCancelamento> {
    const comBoleto = faturas.filter(f => !!f.num_cobranca_boleto);
    if (comBoleto.length === 0) return { resultados: [], falhas: [], rotulosFalha: [] };

    const rotulo = (f: FaturaCancelavel) => f.mes_referencia || `fatura ${f.id}`;

    const { data: webhookData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'boleto_cancellation_webhook_url')
        .single();

    if (!webhookData?.value) {
        const resultados = comBoleto.map<ResultadoCancelamento>(f => ({
            fatura_id: f.id,
            mes_referencia: rotulo(f),
            nosso_numero: f.num_cobranca_boleto ?? null,
            conta_sicoob: null,
            ok: false,
            jaEstavaBaixado: false,
            detalhe: 'Webhook de cancelamento não configurado (system_settings.boleto_cancellation_webhook_url)',
        }));
        await persistir(resultados);
        return { resultados, falhas: resultados, rotulosFalha: resultados.map(r => r.mes_referencia) };
    }

    // O n8n espera a fatura inteira no corpo (paridade com o cancelamento manual
    // do BoletoEmitidoModal), não só o nossoNumero.
    const { data: fullRows } = await supabase
        .from('faturas')
        .select('*')
        .in('id', comBoleto.map(f => f.id));
    const fullMap = new Map((fullRows || []).map((f: any) => [String(f.id), f]));

    const resultados: ResultadoCancelamento[] = [];

    for (const fatura of comBoleto) {
        const faturaFull = fullMap.get(String(fatura.id)) || fatura;
        const conta = await resolveContaSicoob(faturaFull);
        const nossoNumero = String(fatura.num_cobranca_boleto);

        const base = {
            fatura_id: fatura.id,
            mes_referencia: rotulo(fatura),
            nosso_numero: nossoNumero,
            conta_sicoob: conta,
        };

        try {
            const resp = await fetch(webhookData.value, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: fatura.id,
                    nossoNumero,
                    motivoCancelamento,
                    ...faturaFull,
                    emitir_em_vigor_gestao: conta === 'gestao',
                    conta_sicoob: conta,
                }),
            });

            const corpo = (await resp.text()).trim();

            // n8n devolve 200 com corpo vazio quando o fluxo morre antes do nó
            // "Respond to Webhook". 200 sozinho não prova que cancelou.
            if (!corpo) {
                resultados.push({
                    ...base, ok: false, jaEstavaBaixado: false,
                    detalhe: `HTTP ${resp.status} com corpo vazio — o fluxo n8n falhou antes de responder`,
                });
                continue;
            }

            let statusErro = false;
            try {
                const json = JSON.parse(corpo);
                statusErro = json?.status === 'erro';
            } catch { /* resposta não-JSON: decide por resp.ok + texto */ }

            if (resp.ok && !statusErro) {
                resultados.push({ ...base, ok: true, jaEstavaBaixado: false, detalhe: `HTTP ${resp.status} ${corpo.slice(0, 300)}` });
                continue;
            }

            const { ok, jaBaixado } = classificarRespostaCancelamento(corpo);
            resultados.push({
                ...base, ok, jaEstavaBaixado: jaBaixado,
                detalhe: `HTTP ${resp.status} ${corpo.slice(0, 300)}`,
            });
        } catch (err: any) {
            resultados.push({
                ...base, ok: false, jaEstavaBaixado: false,
                detalhe: `Falha de rede: ${err?.message || String(err)}`,
            });
        }
    }

    await persistir(resultados);
    const falhas = resultados.filter(r => !r.ok);
    return { resultados, falhas, rotulosFalha: falhas.map(r => r.mes_referencia) };
}

/**
 * Carimba a divergência na fatura. Sucesso limpa o campo — inclusive o de uma
 * tentativa anterior que falhou, para a fila não acumular resolvido.
 */
async function persistir(resultados: ResultadoCancelamento[]): Promise<void> {
    const ok = resultados.filter(r => r.ok).map(r => r.fatura_id);
    const falhas = resultados.filter(r => !r.ok);

    try {
        if (ok.length > 0) {
            await supabase
                .from('faturas')
                .update({ cancelamento_sicoob_erro: null, cancelamento_sicoob_erro_em: null })
                .in('id', ok as any[]);
        }
        // Detalhe é por fatura, então não dá para agrupar num update só.
        await Promise.all(falhas.map(r =>
            supabase
                .from('faturas')
                .update({
                    cancelamento_sicoob_erro: `[boleto ${r.nosso_numero} · conta ${r.conta_sicoob ?? '?'}] ${r.detalhe}`,
                    cancelamento_sicoob_erro_em: new Date().toISOString(),
                })
                .eq('id', r.fatura_id)
        ));
    } catch (err) {
        // Registrar a divergência é best-effort; não pode derrubar o fluxo principal.
        console.warn('Falha ao gravar cancelamento_sicoob_erro', err);
    }
}
